import { useState } from 'react'
import { useTenantConfig, useCartActions } from '@webmono/storefront-ui/providers'
import type { LoyaltyRedemption as LoyaltyRedemptionType, RedemptionOption } from '@webmono/storefront-ui/types'
import { formatCents, cn } from '@webmono/storefront-ui/utils'

interface LoyaltyRedemptionProps {
  currentPoints: number
  currentTier: string
  selectedRedemption?: LoyaltyRedemptionType
}

export function LoyaltyRedemption({ currentPoints, currentTier, selectedRedemption }: LoyaltyRedemptionProps) {
  const tenant = useTenantConfig()
  const { setLoyaltyRedemption } = useCartActions()
  const [isExpanded, setIsExpanded] = useState(false)

  if (!tenant.loyalty?.enabled || !tenant.features.loyalty) {
    return null
  }

  const { programName, redemptionOptions, tiers } = tenant.loyalty

  const currentTierData = tiers.find((t) => t.id === currentTier) || tiers[0]
  const nextTier = tiers.find((t) => t.minPoints > currentPoints)

  const handleSelect = (option: RedemptionOption) => {
    if (currentPoints < option.pointsCost) return

    if (selectedRedemption?.optionId === option.id) {
      setLoyaltyRedemption(undefined)
    } else {
      setLoyaltyRedemption({
        pointsUsed: option.pointsCost,
        discountCents: option.discountCents,
        optionId: option.id,
        optionName: option.name,
      })
    }
  }

  const handleClear = () => {
    setLoyaltyRedemption(undefined)
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-left">
            <h3 className="font-medium text-foreground">{programName}</h3>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-primary">{currentPoints.toLocaleString()}</span> points available
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedRedemption && (
            <span className="text-sm font-medium text-success">
              -{formatCents(selectedRedemption.discountCents, tenant.operations.currency)}
            </span>
          )}
          <svg
            className={cn('w-5 h-5 text-muted-foreground transition-transform', isExpanded && 'rotate-180')}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-border p-4 space-y-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className={cn(
                'px-2 py-0.5 rounded-full text-xs font-medium',
                currentTierData.id === 'gold' && 'bg-yellow-100 text-yellow-800',
                currentTierData.id === 'silver' && 'bg-gray-100 text-gray-800',
                (currentTierData.id === 'bronze' || currentTierData.id === 'member') && 'bg-orange-100 text-orange-800'
              )}>
                {currentTierData.name}
              </span>
              <span className="text-muted-foreground">{currentTierData.multiplier}x points</span>
            </div>
            {nextTier && (
              <span className="text-muted-foreground">
                {(nextTier.minPoints - currentPoints).toLocaleString()} pts to {nextTier.name}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">Redeem Points</h4>
            {redemptionOptions.map((option) => {
              const isSelected = selectedRedemption?.optionId === option.id
              const canAfford = currentPoints >= option.pointsCost

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleSelect(option)}
                  disabled={!canAfford && !isSelected}
                  className={cn(
                    'w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all text-left',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : canAfford
                      ? 'border-border hover:border-primary/50'
                      : 'border-border opacity-50 cursor-not-allowed'
                  )}
                >
                  <div>
                    <p className="font-medium text-foreground">{option.name}</p>
                    {option.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <p className={cn(
                      'font-semibold',
                      canAfford ? 'text-primary' : 'text-muted-foreground'
                    )}>
                      {option.pointsCost.toLocaleString()} pts
                    </p>
                    <p className="text-xs text-success">
                      Save {formatCents(option.discountCents, tenant.operations.currency)}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>

          {selectedRedemption && (
            <button
              type="button"
              onClick={handleClear}
              className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Remove reward
            </button>
          )}

          <p className="text-xs text-muted-foreground text-center">
            Earn {tenant.loyalty.pointsPerDollar} points per $1 spent
          </p>
        </div>
      )}
    </div>
  )
}
